import { ensureToken } from './authApi';
import { AUTH_URL } from './apiConfig';

// ── AI Actions ──────────────────────────────────────────────────────────────
// Generic, config-driven "call an AI/automation service and get a JSON
// response back" call. The gateway resolves WHICH service/endpoint to hit
// entirely from admin config (module/group/field/action) — this file never
// carries a service URL, module name, or field name as a hardcoded value.

/**
 * @param {Object} args
 * @param {string} args.module
 * @param {string} args.group     - FormGroup.name the field lives in
 * @param {string} args.field     - FormGroupField.field the action is attached to
 * @param {string} args.actionKey - AiActionConfig.key to run
 * @param {Object} [args.inputs]  - { [param]: textValue } for this action's text inputs
 * @param {Object} [args.files]   - { [param]: File } for this action's file inputs
 * @returns {Promise<{ raw: any, actionKey: string, responseMappings: any[] }>}
 */
export async function runAiAction({ module, group, field, actionKey, inputs = {}, files = {} }) {
  if (!module || !group || !field || !actionKey) {
    throw new Error('module, group, field and actionKey are required to run an AI action');
  }
  const token = await ensureToken();

  const formData = new FormData();
  formData.append('json', JSON.stringify({ inputs }));
  Object.entries(files).forEach(([param, file]) => {
    if (file) formData.append(param, file);
  });

  const params = new URLSearchParams({ module, group, field, action: actionKey });
  const res = await fetch(`${AUTH_URL}/ai-action?${params.toString()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const { logout } = await import('./authApi');
  if (res.status === 401) logout();

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const error = new Error(data?.error || data?.message || `API ${res.status}: ${res.statusText}`);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data?.data ?? data;
}
